import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  NgModule,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { BreakpointsService } from '@core/services/breakpoints/breakpoints.service';
import { SearchEngineFieldLocation } from '@domains/search-engine/entities/location-search-field';
import { SearchEngineQueryParams } from '@domains/search-engine/search-engine-field';
import { EuresAlgoliaFilterItem } from '@models/eures/search-filters';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { EuresSearchFiltersWebservice } from '@webservices/eures-algolia-search/search-filters.webservice';
import { NgUpperFirstPipeModule } from '@wizbii/angular-pipes';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first, map, shareReplay, switchMap } from 'rxjs/operators';

export interface LocationEures {
  name: string;
  code: string;
}

@UntilDestroy()
@Component({
  selector: 'app-location-search-eures',
  templateUrl: './location-search-eures.component.html',
  styleUrls: ['./location-search-eures.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => LocationSearchEuresComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationSearchEuresComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() field!: SearchEngineFieldLocation;
  @Input() modalOpen!: boolean;
  @Input() liveSearch!: boolean;

  @Output() hasChanged = new EventEmitter<boolean>();

  locationIsFocused = false;

  private _value?: LocationEures;

  countries$ = new Observable<EuresAlgoliaFilterItem[]>();

  error$ = new BehaviorSubject<boolean>(false);

  get isMobile$(): Observable<boolean> {
    return this.breakpointsService.isMobile$;
  }

  set value(val: LocationEures | undefined) {
    if (this._value === val) {
      return;
    }
    this._value = val;
    this.onChange(val);
    this.onTouched();
  }

  get value(): LocationEures | undefined {
    return this._value;
  }

  location = new FormControl(undefined);

  constructor(
    private readonly breakpointsService: BreakpointsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly euresSearchFiltersWebservice: EuresSearchFiltersWebservice,
    private readonly route: ActivatedRoute
  ) {}

  // eslint-disable-next-line sonarjs/cognitive-complexity
  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(
        map((q) => q.get(SearchEngineQueryParams.Location)),
        filter((code) => !!code),
        switchMap((code) =>
          code
            ? this.euresSearchFiltersWebservice.getCountries('', {
                hitsPerPage: 1,
                typoTolerance: false,
                filters: `objectID:${code}`,
              })
            : of(undefined)
        ),
        map((response) => response?.hits[0]),
        untilDestroyed(this)
      )
      .subscribe((country: any) => {
        this.location.setValue(country ? { name: country.name, code: country.objectID } : undefined);
      });

    this.location.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((val) => !!val?.name)
      )
      .subscribe((val) => {
        this.value = val;

        if (this.liveSearch) {
          this.hasChanged.emit();
        }
      });

    this.countries$ = this.location.valueChanges.pipe(
      debounceTime(300),
      untilDestroyed(this),
      filter((query) => query && query.length > 0),
      distinctUntilChanged(),
      switchMap((query) => this.euresSearchFiltersWebservice.getCountries(query)),
      map((result) => result.hits),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.countries$
      .pipe(
        untilDestroyed(this),
        map((countries) => countries?.length === 0)
      )
      .subscribe({
        next: (val) => {
          this.error$.next(val);
        },
      });
  }

  onClose(): void {
    if (this.value) {
      return;
    }

    this.countries$
      .pipe(
        first(),
        map((countries) => countries[0]),
        filter((country) => !!country)
      )
      .subscribe({
        next: (country) => {
          this.location.setValue({ name: country.name, code: country.objectID });
        },
      });

    if (this.liveSearch) {
      this.hasChanged.emit();
    }
  }

  clearValue(event: any): void {
    this.location.setValue(undefined);
    this.value = undefined;
    this.error$.next(false);

    if (this.liveSearch) {
      this.hasChanged.emit();
    }

    event.stopPropagation();
  }

  displayFn(value: any): string {
    return value?.name ?? '';
  }

  onChange: (val?: LocationEures) => void = () => {
    // intentional empty arrow function
  };
  onTouched: () => void = () => {
    // intentional empty arrow function
  };

  writeValue(val?: LocationEures): void {
    if (val) {
      this.value = val;
    }
  }

  registerOnChange(fn: (val?: LocationEures) => void): void {
    this.onChange = (val) => {
      fn(val);
      this.cdr.markForCheck();
    };
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      this.cdr.markForCheck();
    };
  }

  trackByCountryId(_: number, country: EuresAlgoliaFilterItem): string {
    return country?.objectID;
  }

  ngOnDestroy(): void {
    this.error$.complete();
  }
}

@NgModule({
  imports: [
    CommonModule,
    MatIconModule,
    MatOptionModule,
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatSelectModule,
    NgUpperFirstPipeModule,
  ],
  declarations: [LocationSearchEuresComponent],
  exports: [LocationSearchEuresComponent],
})
export class SearchEngineLocationEuresModule {}
